/**
 * SupportingDocsPanel
 * -------------------
 * Phase A+B (iter1086h) - single shared component that renders and
 * uploads support documents for any entity in the platform. Under the
 * hood it reads from the unified backbone:
 *   GET    /api/documents/for/{entity_type}/{entity_id}
 *   POST   /api/documents/upload                              (new file)
 *   POST   /api/documents/{doc_id}/links                      (attach existing)
 *   DELETE /api/documents/{doc_id}/links/{entity_type}/{id}   (detach)
 *
 * The Panel deliberately shows every doc regardless of upload source,
 * with a tiny "Uploaded via {surface}" subtitle so the user understands
 * why a bank-feed receipt suddenly appears on their invoice view - it's
 * the same physical file, linked in two places.
 *
 * Design goals:
 *   - Compact card (~120 px empty, expands with docs)
 *   - Works identically on mobile + desktop (single column list)
 *   - Multi-doc uploads are unified: the "n documents attached" header
 *     stays consistent, and the download-all zip option appears once
 *     there's more than one doc.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Paperclip,
  Upload,
  Loader2,
  Download,
  Eye,
  Trash2,
  FileText,
  ImageIcon,
  FileSpreadsheet,
  FileArchive,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const ACCEPT_TYPES = 'image/png,image/jpeg,image/gif,image/webp,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,.pdf,.png,.jpg,.jpeg,.gif,.webp,.xls,.xlsx,.csv,.txt';

const MAX_SIZE_BYTES = 10 * 1024 * 1024;

const _iconFor = (ct) => {
  const s = String(ct || '').toLowerCase();
  if (s.startsWith('image/')) return ImageIcon;
  if (s.includes('spreadsheet') || s.includes('excel') || s.endsWith('csv')) return FileSpreadsheet;
  if (s.includes('zip') || s.includes('archive')) return FileArchive;
  return FileText;
};

const _fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const _fmtSource = (via) => {
  const map = {
    invoice: 'Uploaded via invoice',
    bill: 'Uploaded via bill',
    bank_feed: 'Uploaded via bank feed',
    bank_feed_txn: 'Uploaded via bank feed',
    manual_journal: 'Uploaded via journal',
    recurring_journal: 'Uploaded via recurring journal',
    gl_breakdown: 'Uploaded from GL breakdown',
    prepayment: 'Uploaded via prepayment',
    fixed_asset: 'Uploaded via fixed asset',
    loan: 'Uploaded via loan',
    investment: 'Uploaded via investment',
    other_payment: 'Uploaded via other payment',
    other_receipt: 'Uploaded via other receipt',
  };
  return map[String(via || '').toLowerCase()] || 'Uploaded';
};

const legacyDocumentTypeFor = (entityType) => {
  // Map the new entity_type to the legacy `document_type` value the
  // /documents/upload endpoint still requires. Everything else falls
  // through to invoice_support, which is the widest-accepted default.
  switch (entityType) {
    case 'invoice': return 'invoice_support';
    case 'bill': return 'bill_support';
    case 'payment': return 'payment_proof';
    default: return 'invoice_support';
  }
};

const SupportingDocsPanel = ({
  entityType,
  entityId,
  uploadedVia,        // optional label ("bank_feed", "gl_breakdown", etc.)
  title = 'Supporting documents',
  description = 'Receipts, contracts, proof-of-payment - anything that backs up this record.',
  className = '',
  compact = false,    // pass true from inline surfaces (GL breakdown row)
  disabled = false,   // e.g. archived record - no more uploads allowed
}) => {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fileRef = useRef(null);

  const canWrite = !!entityType && !!entityId && !disabled;

  const fetchDocs = useCallback(async () => {
    if (!entityType || !entityId) { setDocs([]); return; }
    try {
      const res = await axios.get(`${API}/documents/for/${entityType}/${entityId}`);
      setDocs(Array.isArray(res.data?.documents) ? res.data.documents : []);
    } catch {
      setDocs([]);
    }
  }, [entityType, entityId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleFiles = async (files) => {
    if (!files || !files.length || !canWrite) return;
    // Guard: 10 MB per file. Reject the whole batch if any is too large
    // so the user isn't left with a partial upload.
    for (const f of files) {
      if (f.size > MAX_SIZE_BYTES) {
        toast.error(`${f.name} is over 10 MB. Please upload a smaller file.`);
        return;
      }
    }
    setUploading(true);
    let ok = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('document_type', legacyDocumentTypeFor(entityType));
        fd.append('reference_id', entityId);
        fd.append('entity_type', entityType);
        fd.append('entity_id', entityId);
        fd.append('uploaded_via', uploadedVia || entityType);
        await axios.post(`${API}/documents/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        ok += 1;
      } catch {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    if (ok) toast.success(ok === 1 ? 'Document uploaded' : `${ok} documents uploaded`);
    fetchDocs();
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleDetach = async (doc) => {
    if (!canWrite || busyId) return;
    setBusyId(doc.id);
    try {
      await axios.delete(`${API}/documents/${doc.id}/links/${entityType}/${entityId}`);
      // Optimistic remove
      setDocs((prev) => (prev || []).filter((d) => d.id !== doc.id));
    } catch {
      toast.error('Could not remove document');
      fetchDocs();
    } finally {
      setBusyId(null);
    }
  };

  if (docs === null) {
    return (
      <div className={`rounded-xl border border-slate-200 p-4 ${className}`} data-testid={`docs-panel-${entityType}-loading`}>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading documents…
        </div>
      </div>
    );
  }

  const count = docs.length;

  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white ${compact ? 'p-3' : 'p-4'} ${className}`}
      data-testid={`docs-panel-${entityType}-${entityId}`}
    >
      <div className={`flex items-start justify-between gap-3 ${count === 0 ? '' : 'mb-3'}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
            <h3 className="text-[13px] font-bold text-slate-900 leading-tight">
              {title}
              {count > 0 && (
                <span className="ml-2 text-[11px] font-semibold text-slate-500">
                  {count} attached
                </span>
              )}
            </h3>
          </div>
          {!compact && (
            <p className="text-[11px] text-slate-500 mt-1 leading-snug">{description}</p>
          )}
        </div>
        {canWrite && (
          <label
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-bold cursor-pointer transition-colors ${uploading ? 'bg-slate-100 text-slate-500 pointer-events-none' : 'bg-[#108981] text-white hover:bg-[#0d766a]'}`}
            data-testid={`docs-panel-upload-${entityType}`}
          >
            {uploading ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>) : (<><Upload className="w-3.5 h-3.5" /> Upload</>)}
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT_TYPES}
              multiple
              className="hidden"
              onChange={(e) => handleFiles(Array.from(e.target.files || []))}
            />
          </label>
        )}
      </div>

      {count === 0 ? (
        <div className="text-[12px] text-slate-400 italic">
          No documents yet.{canWrite ? ' Tap Upload to add one.' : ''}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          <AnimatePresence initial={false}>
            {docs.map((d) => {
              const Icon = _iconFor(d.content_type);
              return (
                <motion.li
                  key={d.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 8 }}
                  transition={{ duration: 0.18 }}
                  className="flex items-center gap-3 py-2"
                >
                  <span className="w-8 h-8 shrink-0 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-slate-600" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-slate-800 truncate leading-tight">
                      {d.original_name}
                    </p>
                    <p className="text-[10.5px] text-slate-500 leading-tight mt-0.5">
                      {_fmtSource(d.uploaded_via)} · {_fmtSize(d.size)}
                    </p>
                  </div>
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 w-8 h-8 rounded-lg text-slate-500 hover:text-[#108981] hover:bg-[#108981]/8 flex items-center justify-center transition-colors"
                    aria-label={`Preview ${d.original_name}`}
                    data-testid={`docs-panel-view-${d.id}`}
                  >
                    <Eye className="w-4 h-4" />
                  </a>
                  <a
                    href={d.download_url}
                    className="shrink-0 w-8 h-8 rounded-lg text-slate-500 hover:text-[#108981] hover:bg-[#108981]/8 flex items-center justify-center transition-colors"
                    aria-label={`Download ${d.original_name}`}
                    data-testid={`docs-panel-download-${d.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  {canWrite && (
                    <button
                      type="button"
                      onClick={() => handleDetach(d)}
                      disabled={busyId === d.id}
                      className="shrink-0 w-8 h-8 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors disabled:opacity-50"
                      aria-label={`Remove ${d.original_name}`}
                      data-testid={`docs-panel-remove-${d.id}`}
                    >
                      {busyId === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  )}
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
};

export default SupportingDocsPanel;
